import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiCheck, FiChevronDown, FiChevronUp, FiChevronRight, FiUpload, FiFile } from 'react-icons/fi';
import { formsAPI, responsesAPI } from '../services/api';
import CalculationField from '../components/CalculationField';
import MatrixField from '../components/MatrixField';
import TableField from '../components/TableField';
import MapLocationField from '../components/MapLocationField';
import { isQuestionnaire } from '../utils/questionnaireScoring';

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f3f4f6;
  border: none;
  border-radius: 6px;
  color: #374151;
  cursor: pointer;
  margin-bottom: 32px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #e5e7eb;
  }
`;

const FormCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 40px;
`;

const FormTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 16px;
  text-align: center;
`;

const FormDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 40px;
  text-align: center;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const FormField = styled.div.withConfig({
  shouldForwardProp: (prop) => !['fieldWidth', 'fieldType'].includes(prop),
})`
  margin-bottom: 24px;
  
  /* Grid column span based on fieldWidth */
  grid-column: ${props => {
    if (props.fieldType === 'section' || props.fieldType === 'heading' || props.fieldType === 'subheading') {
      return 'span 12'; // Always full width for sections/headings
    }
    if (props.fieldWidth === 'full') return 'span 12';
    if (props.fieldWidth === 'half') return 'span 6';
    if (props.fieldWidth === 'third') return 'span 4';
    if (props.fieldWidth === 'two-thirds') return 'span 8';
    if (props.fieldWidth === 'quarter') return 'span 3';
    return 'span 12'; // default to full width
  }};
  
  @media (max-width: 768px) {
    grid-column: span 12 !important; /* Force full width on mobile */
  }
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FieldLabel = styled.label.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic'].includes(prop),
})`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  font-size: ${props => props.fontSize || '14px'};
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : '500'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  
  ${props => props.required && `
    &::after {
      content: ' *';
      color: #ef4444;
    }
  `}
`;

const FieldInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft'].includes(prop),
})`
  width: 100%;
  padding: 12px 16px 12px ${props => {
    const paddingLeft = parseInt(props.inputPaddingLeft) || 16;
    console.log('FieldInput padding props:', props.inputPaddingLeft, 'parsed:', paddingLeft);
    return paddingLeft;
  }}px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:invalid {
    border-color: #ef4444;
  }
`;

const FieldTextarea = styled.textarea.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft'].includes(prop),
})`
  width: 100%;
  padding: 12px 16px 12px ${props => {
    const paddingLeft = parseInt(props.inputPaddingLeft) || 16;
    console.log('FieldTextarea padding props:', props.inputPaddingLeft, 'parsed:', paddingLeft);
    return paddingLeft;
  }}px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FieldSelect = styled.select.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft'].includes(prop),
})`
  width: 100%;
  padding: 12px 16px 12px ${props => props.inputPaddingLeft || 16}px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const RadioGroup = styled.div.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft'].includes(prop),
})`
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  padding-left: ${props => props.inputPaddingLeft || 0}px;
`;

const RadioItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
`;

const CheckboxGroup = styled.div.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft', 'checkboxLayout'].includes(prop),
})`
  display: flex;
  flex-direction: ${props => props.checkboxLayout === 'horizontal' ? 'row' : 'column'};
  flex-wrap: ${props => props.checkboxLayout === 'horizontal' ? 'wrap' : 'nowrap'};
  gap: 12px;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  padding-left: ${props => props.inputPaddingLeft || 0}px;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
`;

const FieldHeading = styled.h3.withConfig({
  shouldForwardProp: (prop) => prop !== 'level',
})`
  font-size: ${props => {
    switch (props.level) {
      case 1: return '28px';
      case 2: return '24px';
      case 3: return '20px';
      case 4: return '18px';
      case 5: return '16px';
      case 6: return '14px';
      default: return '20px';
    }
  }};
  font-weight: 600;
  color: #1f2937;
  margin: 32px 0 16px 0;
  padding: 0;
  border: none;
  background: none;
  width: 100%;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #059669;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 32px;
  color: #059669;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 24px;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  
  img {
    max-width: 200px;
    max-height: 100px;
    object-fit: contain;
    border-radius: 8px;
  }
`;

const DefaultLogo = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  color: white;
  text-transform: uppercase;
`;

const ContactToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #0277bd;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 12px;
  transition: color 0.2s ease;

  &:hover {
    color: #0369a1;
  }

  svg {
    transition: transform 0.2s ease;
  }
`;

const ContactFieldsContainer = styled.div`
  transition: max-height 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
  max-height: ${props => props.collapsed ? '0' : '1000px'};
  opacity: ${props => props.collapsed ? '0' : '1'};
`;

const FileUploadContainer = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  background: #f9fafb;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }

  &.drag-over {
    border-color: #3b82f6;
    background: #eff6ff;
  }
`;

const FileUploadInput = styled.input`
  display: none;
`;

const FileUploadText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #6b7280;
`;

const FileUploadIcon = styled.div`
  font-size: 32px;
  color: #9ca3af;
`;

// FileUploadButton component removed as it was unused

const FileList = styled.div`
  margin-top: 12px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 8px;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #374151;
`;

const FileName = styled.div`
  font-weight: 500;
  color: #1f2937;
`;

const FileSize = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const RemoveFileButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #dc2626;
  }
`;

function FormPreview() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [contactBlockCollapsed, setContactBlockCollapsed] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [repeatableInstances, setRepeatableInstances] = useState({}); // Track instances for each repeatable field
  const [repeatableContinueStates, setRepeatableContinueStates] = useState({}); // Track continue state for each instance

  // Check if this is a preview mode (from form builder)
  // Handle /preview/preview route (id will be undefined, but pathname contains 'preview')
  const isPreviewRoute = window.location.pathname.includes('/preview/preview');
  const effectiveId = id || (isPreviewRoute ? 'preview' : null);
  const isPreview = searchParams.get('preview') === 'true' || isPreviewRoute;
  const previewId = searchParams.get('id');
  
  // Debug preview detection (only log once)
  if (!window.previewDebugLogged) {
    console.log('FormPreview: Preview detection debug:', {
      pathname: window.location.pathname,
      id: id,
      effectiveId: effectiveId,
      isPreviewRoute: isPreviewRoute,
      isPreview: isPreview,
      previewId: previewId,
      searchParams: Object.fromEntries(searchParams.entries())
    });
    window.previewDebugLogged = true;
  }

  // Multi-step logic - only calculate when form is loaded
  // Convert enable_multi_step from SQL Server Bit (1/0) to boolean
  // SQL Server returns Bit type as 1 or 0, not true/false
  const enableMultiStepValue = form ? (form.enable_multi_step !== undefined ? form.enable_multi_step : form.enableMultiStep) : false;
  const isMultiStep = enableMultiStepValue === true || enableMultiStepValue === 1 || enableMultiStepValue === '1' || enableMultiStepValue === 'true';
  
  const groupSectionsValue = form?.group_sections_with_questions !== undefined ? form.group_sections_with_questions : form?.groupSectionsWithQuestions;
  const groupSectionsWithQuestions = groupSectionsValue === true || groupSectionsValue === 1 || groupSectionsValue === '1' || groupSectionsValue === 'true';
  const isQuestionnaireType = form?.form_structure && typeof form.form_structure === 'object' && form.form_structure.isQuestionnaire;
  
  // Get all fields for multi-step logic - with null checks
  const allFields = form ? (
    isQuestionnaireType && form.form_structure?.questions 
      ? form.form_structure.questions 
      : (form.form_structure && Array.isArray(form.form_structure) ? form.form_structure : [])
  ) : [];
  
  // Helper function to check if a field should be visible based on conditional logic
  const isFieldVisible = (field) => {
    // Check for nested dropdown visibility first
    if (field.type === 'select' && field.parentDropdown) {
      const parentField = allFields.find(f => 
        f.id === field.parentDropdown || 
        f.name === field.parentDropdown ||
        String(f.id) === String(field.parentDropdown) ||
        String(f.name) === String(field.parentDropdown)
      );
      if (parentField) {
        // Try multiple ways to get the parent value
        const parentValue = formData[parentField.name] || 
                           formData[parentField.id] || 
                           formData[String(parentField.name)] ||
                           formData[String(parentField.id)] ||
                           '';
        // Nested dropdown is only visible if parent has a selected value
        const isVisible = !!parentValue && parentValue !== '';
        return isVisible;
      }
      return false; // Parent not found, hide nested dropdown
    }
    
    // Check for conditional field visibility (non-nested dropdowns)
    if (!field.conditionalField || !field.conditionalValue) {
      return true;
    }
    
    // Find the parent field (select, radio, or checkbox)
    const parentField = allFields.find(f => f.id === field.conditionalField || f.name === field.conditionalField);
    if (!parentField) {
      return true; // If parent not found, show field
    }
    
    // Get the selected value from the parent field
    const selectedValue = formData[parentField.name] || formData[parentField.id];
    
    // Handle checkbox fields (array of values)
    if (parentField.type === 'checkbox') {
      const selectedArray = Array.isArray(selectedValue) ? selectedValue : [];
      // Field is visible if the checkbox array includes the conditional value
      return selectedArray.includes(field.conditionalValue);
    }
    
    // Handle select/radio fields (single value)
    // Field is visible only if the selected value matches the conditional value
    return selectedValue === field.conditionalValue;
  };
  
  // Check if there's an intro section (form fields for questionnaires)
  const hasIntroSection = isQuestionnaireType && form?.form_structure?.formFields && form.form_structure.formFields.length > 0;
  
  // Calculate steps based on grouping option
  let totalSteps = 1;
  let stepGroups = [];
  
  if (isMultiStep) {
    if (groupSectionsWithQuestions) {
      // Group sections with their following questions (respecting conditional visibility)
      const visibleFields = allFields.filter(field => 
        field.visible !== false && 
        isFieldVisible(field)
      );
      let currentGroup = { section: null, fields: [] };
      
      visibleFields.forEach(field => {
        if (field.type === 'section') {
          if (currentGroup.section || currentGroup.fields.length > 0) {
            stepGroups.push(currentGroup);
          }
          currentGroup = { section: field, fields: [] };
        } else if (field.type === 'subheading' || field.type === 'content') {
          // Always include subheading and content fields within groups
          currentGroup.fields.push(field);
        } else if (field.type !== 'heading') {
          // Include all other fields (excluding legacy heading fields)
          currentGroup.fields.push(field);
        }
      });
      
      if (currentGroup.section || currentGroup.fields.length > 0) {
        stepGroups.push(currentGroup);
      }
      
      
      totalSteps = hasIntroSection ? stepGroups.length + 1 : stepGroups.length;
    } else {
      // Original behavior: each question is a separate step
      const filteredFields = allFields.filter(field => 
        field.visible !== false && 
        field.type !== 'section' &&
        field.type !== 'subheading' &&
        field.type !== 'content' &&
        isFieldVisible(field)
      );
      totalSteps = hasIntroSection ? filteredFields.length + 1 : filteredFields.length;
    }
  }
  
  // Determine what to show based on current step
  const showIntroOnly = isMultiStep && hasIntroSection && currentStep === 0;
  const showQuestionsOnly = isMultiStep && !showIntroOnly;
  
  // For questions, adjust the index if there's an intro section
  const questionIndex = hasIntroSection ? currentStep - 1 : currentStep;
  
  // Get filtered fields for non-grouped multi-step
  const filteredFields = allFields.filter(field => 
    field.visible !== false && 
    field.type !== 'section' &&
    isFieldVisible(field)
  );
  
  // Get visible fields based on grouping option
  let visibleFields;
  if (isMultiStep && showQuestionsOnly) {
    if (groupSectionsWithQuestions) {
      // Show the current step group (section + questions)
      const stepIndex = hasIntroSection ? currentStep - 1 : currentStep;
      const currentGroup = stepGroups[stepIndex];
      if (currentGroup) {
        // Filter fields in the group based on conditional visibility
        const visibleGroupFields = (currentGroup.fields || []).filter(isFieldVisible);
        visibleFields = [currentGroup.section, ...visibleGroupFields].filter(Boolean);
      } else {
        visibleFields = [];
      }
    } else {
      // Original behavior: show single question
      visibleFields = [filteredFields[questionIndex]].filter(Boolean);
    }
  } else {
    visibleFields = allFields.filter(field => 
      field.visible !== false && 
      isFieldVisible(field)
    );
  }
  
  const isLastStep = currentStep === totalSteps - 1;

  console.log('FormPreview initialization:', {
    urlId: id,
    pathname: window.location.pathname,
    isPreviewRoute,
    effectiveId,
    isPreview,
    previewId
  });

  const handleFileUpload = (fieldName, files) => {
    const field = form?.form_structure?.find(f => f.name === fieldName);
    if (!field || !form) return;
    
    // Validate files
    const maxSize = (field.maxFileSize || 10) * 1024 * 1024; // Convert MB to bytes
    const allowedTypes = field.acceptedFileTypes || [];
    const allowMultiple = field.multipleFiles || false;
    
    const fileArray = Array.from(files);
    
    // Check if multiple files are allowed
    if (!allowMultiple && fileArray.length > 1) {
      toast.error('Only one file is allowed for this field');
      return;
    }
    
    for (const file of fileArray) {
      // Check file size
      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum size is ${field.maxFileSize || 10}MB`);
        return;
      }
      
      // Check file type
      if (allowedTypes.length > 0) {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (!allowedTypes.includes(fileExtension)) {
          toast.error(`File ${file.name} is not an allowed type. Allowed types: ${allowedTypes.join(', ')}`);
          return;
        }
      }
    }
    
    // Add files to state
    setUploadedFiles(prev => ({
      ...prev,
      [fieldName]: allowMultiple ? [...(prev[fieldName] || []), ...fileArray] : fileArray
    }));
  };

  const removeFile = (fieldName, index) => {
    setUploadedFiles(prev => ({
      ...prev,
      [fieldName]: prev[fieldName]?.filter((_, i) => i !== index) || []
    }));
  };

  // Helper function to format accept attribute for file input
  const formatAcceptAttribute = (acceptedTypes) => {
    if (!acceptedTypes || acceptedTypes.length === 0) return '';
    // Map file extensions to proper accept format (with dots and MIME types for videos)
    const mimeTypeMap = {
      'mp4': 'video/mp4,.mp4',
      'mov': 'video/quicktime,.mov',
      'avi': 'video/x-msvideo,.avi',
      'mkv': 'video/x-matroska,.mkv',
      'webm': 'video/webm,.webm'
    };
    return acceptedTypes.map(type => {
      if (mimeTypeMap[type.toLowerCase()]) {
        return mimeTypeMap[type.toLowerCase()];
      }
      return `.${type}`;
    }).join(',');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const loadForm = async () => {
    try {
      setLoading(true);
      const response = await formsAPI.getById(id);
      setForm(response.data.data);
      
      // Debug: Log the form structure to see what fields we have
      console.log('FormPreview: Form loaded with structure:', response.data.data.form_structure);
      if (response.data.data.form_structure && Array.isArray(response.data.data.form_structure)) {
        console.log('FormPreview: First few fields:', response.data.data.form_structure.slice(0, 3).map(f => ({
          id: f.id,
          name: f.name,
          type: f.type,
          label: f.label,
          question: f.question,
          hasLabel: !!f.label,
          hasQuestion: !!f.question
        })));
      }
    } catch (error) {
      toast.error('Failed to load form');
      console.error('Load form error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only log once per component mount
    if (!window.previewUseEffectLogged) {
      console.log('FormPreview useEffect - id:', effectiveId, 'isPreview:', isPreview);
      console.log('FormPreview: Current URL:', window.location.href);
      console.log('FormPreview: Pathname:', window.location.pathname);
      console.log('FormPreview: Search params:', window.location.search);
      console.log('FormPreview: effectiveId:', effectiveId, 'isPreviewRoute:', isPreviewRoute);
      window.previewUseEffectLogged = true;
    }
    
    if (isPreview && previewId) {
      // Handle preview mode with form data from localStorage
      try {
        const formData = localStorage.getItem(previewId);
        if (formData) {
          const parsedFormData = JSON.parse(formData);
          setForm(parsedFormData);
          // Clean up the localStorage after use
          localStorage.removeItem(previewId);
        } else {
          console.error('Preview data not found in localStorage');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error parsing preview data:', error);
        setLoading(false);
      }
    } else if (effectiveId === 'preview' || isPreviewRoute) {
      // Handle preview from questionnaire builder (uses 'previewForm' key)
      try {
        console.log('Preview mode detected, loading from localStorage...');
        const previewData = localStorage.getItem('previewForm');
        console.log('Preview data from localStorage:', previewData?.substring(0, 100) + '...');
        console.log('Available localStorage keys:', Object.keys(localStorage));
        console.log('Looking for key: previewForm');
        
        if (previewData) {
          const parsedFormData = JSON.parse(previewData);
          console.log('Parsed preview form data:', parsedFormData);
          console.log('Is questionnaire?', parsedFormData.form_structure?.isQuestionnaire);
          setForm(parsedFormData);
          console.log('Form state set successfully');
        } else {
          console.error('Preview data not found in localStorage with key "previewForm"');
          console.log('Available localStorage keys:', Object.keys(localStorage));
          
          // Try to find any preview data in localStorage
          const allKeys = Object.keys(localStorage);
          const previewKeys = allKeys.filter(key => key.includes('preview'));
          console.log('Found preview-related keys:', previewKeys);
          
          if (previewKeys.length > 0) {
            console.log('Attempting to load from alternative key:', previewKeys[0]);
            const alternativeData = localStorage.getItem(previewKeys[0]);
            if (alternativeData) {
              try {
                const parsedAlternativeData = JSON.parse(alternativeData);
                console.log('Successfully loaded from alternative key:', previewKeys[0]);
                setForm(parsedAlternativeData);
                return;
              } catch (error) {
                console.error('Error parsing alternative preview data:', error);
              }
            }
          }
          
          toast.error('Preview data not found. Please try again.');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error parsing preview data:', error);
        console.error('Error details:', error.message, error.stack);
        toast.error('Error loading preview');
        setLoading(false);
      }
    } else if (effectiveId && effectiveId !== 'preview') {
      // Handle normal form loading
      loadForm();
    } else {
      console.log('No valid id or preview mode, showing error');
      setLoading(false);
    }
  }, [effectiveId, isPreview, previewId, isPreviewRoute, loadForm]);

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value,
    }));

    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null,
      }));
    }
  };

  const handleNext = () => {
    // Debug: Force breakpoint here
    debugger; // This will pause execution if DevTools is open
    
    // Try multiple logging methods
    try {
      console.log('🚀 handleNext called - Starting validation');
      console.error('🚀 handleNext ERROR LEVEL');
      console.warn('🚀 handleNext WARN LEVEL');
      console.info('🚀 handleNext INFO LEVEL');
      
      // Also try direct DOM manipulation to verify execution
      const testDiv = document.getElementById('__debug-next-clicked');
      if (testDiv) {
        testDiv.textContent = 'Next clicked at ' + new Date().toLocaleTimeString();
      } else {
        const div = document.createElement('div');
        div.id = '__debug-next-clicked';
        div.style.cssText = 'position:fixed;top:10px;right:10px;background:red;color:white;padding:10px;z-index:99999;';
        div.textContent = 'Next clicked at ' + new Date().toLocaleTimeString();
        document.body.appendChild(div);
      }
    } catch (e) {
      alert('Error in handleNext: ' + e.message);
    }
    
    console.log('Current state:', {
      currentStep,
      totalSteps,
      isMultiStep,
      groupSectionsWithQuestions,
      showIntroOnly,
      showQuestionsOnly,
      hasIntroSection
    });
    
    // Validate current step before moving to next
    let hasErrors = false;
    const newErrors = {};
    let currentGroup = null; // Track current group for error messages

    if (showIntroOnly) {
      // Validate form fields
      if (form?.form_structure?.formFields) {
        form.form_structure.formFields.forEach((field) => {
          const fieldKey = field.name || field.id;
          const value = formData[fieldKey];
          if (field.required && (!value || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0))) {
            newErrors[fieldKey] = `${field.label} is required`;
            hasErrors = true;
          }
        });
      }
    } else if (showQuestionsOnly) {
      // Validate all fields in current step
      if (groupSectionsWithQuestions) {
        // Validate all fields in the current step group
        const stepIndex = hasIntroSection ? currentStep - 1 : currentStep;
        currentGroup = stepGroups[stepIndex]; // Store for error messages
        
        if (currentGroup && currentGroup.fields && currentGroup.fields.length > 0) {
          // Use the same visible fields that are actually rendered to ensure we validate correctly
          // Filter out sections and only get actual input fields
          const fieldsToValidate = currentGroup.fields.filter(field => {
            const isVisible = field.visible !== false;
            const isNotSection = field.type !== 'section';
            const hasNameOrId = field.name || field.id;
            return isVisible && isNotSection && hasNameOrId;
          });
          
          // Only validate fields that are explicitly required
          // Check for various ways required might be set
          const requiredFields = fieldsToValidate.filter(field => {
            const req = field.required;
            return req === true || 
                   req === 1 || 
                   req === 'true' || 
                   req === 'required' ||
                   (typeof req === 'string' && req.toLowerCase() === 'true');
          });
          
          // Debug: Log validation details - ALWAYS log
          console.log('[Validation] ==================== VALIDATION START ====================');
          console.log('[Validation] Step:', stepIndex);
          console.log('[Validation] Total fields to validate:', fieldsToValidate.length);
          console.log('[Validation] Required fields count:', requiredFields.length);
          console.log('[Validation] Current group:', currentGroup);
          console.log('[Validation] Fields in group:', fieldsToValidate.map(f => ({ 
            name: f.name, 
            id: f.id,
            label: f.label, 
            required: f.required, 
            type: f.type,
            visible: f.visible
          })));
          console.log('[Validation] Required fields:', requiredFields.map(f => ({ name: f.name, label: f.label, required: f.required, type: f.type })));
          console.log('[Validation] Form data keys:', Object.keys(formData));
          console.log('[Validation] Form data snapshot:', JSON.stringify(formData, null, 2));
          console.log('[Validation] ==========================================================');
          
          if (requiredFields.length > 0) {
            requiredFields.forEach((field) => {
              // Use field.name first (same as in renderField/onChange handlers)
              const fieldKey = field.name || field.id;
              
              if (!fieldKey) {
                console.warn('[Validation] Field has no name or id:', field);
                return; // Skip validation if no key
              }
              
              const value = formData[fieldKey];
              
              console.log('[Validation] Checking field:', {
                key: fieldKey,
                label: field.label || field.question,
                type: field.type,
                value: value,
                valueType: typeof value
              });
              
              // Check if value is empty
              let isEmpty = false;
              
              if (field.type === 'checkbox') {
                // For checkboxes, value must be an array with at least one item
                isEmpty = !Array.isArray(value) || value.length === 0;
              } else if (field.type === 'radio' || field.type === 'select') {
                // For radio/select, value must exist and not be empty string
                isEmpty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
              } else if (field.type === 'rating') {
                // For rating, value must be a number > 0
                isEmpty = !value || value === 0 || value === '';
              } else {
                // For text fields, value must exist and have content (after trimming)
                // Allow 0 as a valid value for number fields
                if (field.type === 'number') {
                  isEmpty = value === undefined || value === null || value === '';
                } else {
                  isEmpty = value === undefined || value === null || value === '' || (typeof value === 'string' && value.trim() === '');
                }
              }
              
              console.log('[Validation] Field result:', {
                key: fieldKey,
                isEmpty: isEmpty,
                willShowError: isEmpty
              });
              
              if (isEmpty) {
                newErrors[fieldKey] = `${field.label || field.question || 'This field'} is required`;
                hasErrors = true;
                console.error('[Validation] ❌ FAILED:', fieldKey, field.label || field.question);
              } else {
                console.log('[Validation] ✅ PASSED:', fieldKey, field.label || field.question);
              }
            });
          } else {
            console.log('[Validation] No required fields in this step, allowing progression');
          }
        }
      } else {
        // Validate single field (original behavior)
        const currentQuestion = filteredFields[questionIndex];
        if (currentQuestion && currentQuestion.required) {
          const fieldKey = currentQuestion.id || currentQuestion.name;
          const value = formData[fieldKey];
          if (!value || 
              (Array.isArray(value) && value.length === 0) || 
              (typeof value === 'string' && value.trim() === '') ||
              (currentQuestion.type === 'checkbox' && (!Array.isArray(value) || value.length === 0))) {
            newErrors[fieldKey] = `${currentQuestion.label || currentQuestion.question || 'This question'} is required`;
            hasErrors = true;
          }
        }
      }
    }

    console.log('🔍 Validation result:', {
      hasErrors,
      errorsCount: Object.keys(newErrors).length,
      errorKeys: Object.keys(newErrors),
      errors: newErrors
    });

    if (hasErrors) {
      console.error('❌ VALIDATION FAILED - Blocking navigation');
      setErrors(newErrors);
      
      // Get field labels for error message
      const failedFieldLabels = Object.keys(newErrors).map(key => {
        // Try to find field in current group
        let field = null;
        if (currentGroup?.fields) {
          field = currentGroup.fields.find(f => (f.name || f.id) === key);
        }
        // If not found, try filteredFields
        if (!field) {
          field = filteredFields.find(f => (f.name || f.id) === key);
        }
        return field?.label || field?.question || key;
      });
      
      const errorMessage = failedFieldLabels.length > 0 
        ? `Please fill required fields: ${failedFieldLabels.join(', ')}`
        : 'Please answer all required fields before proceeding';
      
      console.error('❌ Showing error toast:', errorMessage);
      toast.error(errorMessage);
      
      // Scroll to first error
      const firstErrorKey = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorKey}"], [id="${firstErrorKey}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      
      return;
    }
    
    console.log('✅ VALIDATION PASSED - Allowing navigation to next step');

    // Clear errors and move to next step
    setErrors({});
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    // Clear errors when going back
    setErrors({});
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form || !form.form_structure) return true;

    form.form_structure.forEach(field => {
      const value = formData[field.name];

      // Check required fields
      if (field.required && (!value || value === '' || (Array.isArray(value) && value.length === 0))) {
        newErrors[field.name] = `${field.label} is required`;
      }

      // Type-specific validation
      if (value && value !== '') {
        switch (field.type) {
          case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
              newErrors[field.name] = 'Please enter a valid email address';
            }
            break;
          case 'number':
            if (isNaN(Number(value))) {
              newErrors[field.name] = 'Please enter a valid number';
            }
            break;
          default:
            // No additional validation for other field types
            break;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isPreview) {
      toast.info('This is a preview. Form cannot be submitted in preview mode.');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    try {
      setSubmitting(true);
      await responsesAPI.submit({
        form_id: id,
        response_data: formData,
      });
      
      setSubmitted(true);
      toast.success('Form submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit form');
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };


  // Helper function to check if a field is a contact block (currently unused)
  // const isContactBlock = (field) => {
  //   return field.type === 'contact';
  // };

  const renderField = (field) => {
    // Debug: Log field data to see what we're working with
    console.log('FormPreview: renderField called for field:', {
      id: field.id,
      name: field.name,
      type: field.type,
      label: field.label,
      question: field.question,
      hasLabel: !!field.label,
      hasQuestion: !!field.question,
      labelOrQuestion: field.label || field.question
    });
    
    const fieldProps = {
      name: field.name,
      required: field.required,
      value: formData[field.name] || '',
      onChange: (e) => handleInputChange(field.name, e.target.value),
      fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : undefined,
      bold: field.bold || false,
      italic: field.italic || false,
      inputPaddingLeft: parseInt(field.inputPaddingLeft) || 16
    };
    
    // Debug logging for styling
    if (field.fontFamily || field.bold || field.italic || field.inputPaddingLeft) {
      console.log('FormPreview: Field with styling:', field.label, {
        fontFamily: field.fontFamily,
        bold: field.bold,
        italic: field.italic,
        inputPaddingLeft: field.inputPaddingLeft
      });
      console.log('FormPreview: fieldProps object:', fieldProps);
    }
    
    // Debug logging (only for fields with styling)
    if (field.fontFamily && field.fontFamily !== 'default') {
      console.log('FormPreview: Field with font family:', field.label, field.fontFamily);
    }
    if (field.marginTop || field.marginBottom || field.paddingTop || field.paddingBottom) {
      console.log('FormPreview: Field with spacing:', field.label, {
        marginTop: field.marginTop,
        marginBottom: field.marginBottom,
        paddingTop: field.paddingTop,
        paddingBottom: field.paddingBottom
      });
    }

    switch (field.type) {
      case 'heading':
        // Legacy heading block is deprecated in favor of 'section'
        // Do not render to avoid duplicate headings when both exist
        console.log('FormPreview: Skipping legacy heading field:', field.label);
        return null;
      
      case 'text':
      case 'email':
      case 'number':
      case 'date':
        return (
          <FieldInput
            type={field.type}
            {...fieldProps}
            placeholder={field.placeholder}
          />
        );

      case 'textarea':
        return (
          <FieldTextarea
            {...fieldProps}
            placeholder={field.placeholder}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          />
        );

      case 'mapLocation': {
        const raw = formData[field.name];
        const mapVal = raw && typeof raw === 'object' && typeof raw.lat === 'number' && typeof raw.lng === 'number'
          ? raw
          : null;
        return (
          <MapLocationField
            value={mapVal}
            onChange={() => {}}
            disabled
          />
        );
      }

      case 'select':
        // Check if this is a nested dropdown
        const isNestedDropdown = field.parentDropdown;
        let parentField = null;
        let parentValue = null;
        let availableNestedOptions = [];
        
        if (isNestedDropdown) {
          // Try multiple ways to find the parent field
          parentField = allFields.find(f => 
            f.id === field.parentDropdown || 
            f.name === field.parentDropdown ||
            String(f.id) === String(field.parentDropdown) ||
            String(f.name) === String(field.parentDropdown)
          );
          if (parentField) {
            // Try multiple ways to get the parent value
            parentValue = formData[parentField.name] || 
                         formData[parentField.id] || 
                         formData[String(parentField.name)] ||
                         formData[String(parentField.id)] ||
                         '';
            // Get nested options based on parent selection
            if (parentValue) {
              availableNestedOptions = parentField.nestedOptions?.[parentValue] || [];
              console.log('FormPreview - Nested dropdown debug:', {
                parentFieldId: parentField.id,
                parentFieldName: parentField.name,
                parentValue: parentValue,
                nestedOptions: parentField.nestedOptions,
                availableNestedOptions: availableNestedOptions,
                nestedOptionsKeys: parentField.nestedOptions ? Object.keys(parentField.nestedOptions) : []
              });
            }
          } else {
            // Parent field not found - this shouldn't happen, but hide the dropdown
            console.warn('Nested dropdown parent field not found:', field.parentDropdown);
            return null;
          }
        }
        
        // If nested dropdown and parent not selected, hide it
        if (isNestedDropdown && (!parentValue || parentValue === '')) {
          return null;
        }
        
        return (
          <FieldSelect 
            {...fieldProps} 
            value={isNestedDropdown ? (formData[field.name] || '') : fieldProps.value}
            disabled={isNestedDropdown && !parentValue}
            onChange={(e) => {
              handleInputChange(field.name, e.target.value);
              
              // Clear dependent conditional fields when selection changes
              const selectedValue = e.target.value;
              const conditionalFields = field.conditionalOptions?.[selectedValue] || [];
              const otherValues = field.options?.filter(opt => opt !== selectedValue) || [];
              otherValues.forEach(otherValue => {
                const fieldsToClear = field.conditionalOptions?.[otherValue] || [];
                fieldsToClear.forEach(fieldId => {
                  const fieldToClear = allFields.find(f => f.id === fieldId);
                  if (fieldToClear) {
                    handleInputChange(fieldToClear.name || fieldToClear.id, '');
                  }
                });
              });
              
              // Clear nested dropdowns that depend on this field when selection changes
              if (field.nestedOptions) {
                const dependentFields = allFields.filter(f => 
                  f.type === 'select' && 
                  (f.parentDropdown === field.id ||
                   f.parentDropdown === field.name ||
                   String(f.parentDropdown) === String(field.id) ||
                   String(f.parentDropdown) === String(field.name))
                );
                dependentFields.forEach(depField => {
                  handleInputChange(depField.name || depField.id, '');
                });
              }
            }}
          >
            <option value="">Select an option</option>
            {isNestedDropdown && availableNestedOptions.length > 0
              ? availableNestedOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))
              : (field.options?.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                )) || [])
            }
          </FieldSelect>
        );

      case 'radio':
        return (
          <RadioGroup
            fontFamily={field.fontFamily}
            bold={field.bold}
            italic={field.italic}
            inputPaddingLeft={field.inputPaddingLeft}
          >
            {field.options?.map((option, index) => (
              <RadioItem key={index}>
                <RadioInput
                  type="radio"
                  name={field.name}
                  value={option}
                  checked={formData[field.name] === option}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                />
                <span>{option}</span>
              </RadioItem>
            ))}
          </RadioGroup>
        );

      case 'checkbox':
        const checkboxValues = formData[field.name] || [];
        return (
          <CheckboxGroup
            fontFamily={field.fontFamily}
            bold={field.bold}
            italic={field.italic}
            inputPaddingLeft={field.inputPaddingLeft}
            checkboxLayout={field.checkboxLayout || 'vertical'}
          >
            {field.options?.map((option, index) => (
              <CheckboxItem key={index}>
                <CheckboxInput
                  type="checkbox"
                  value={option}
                  checked={checkboxValues.includes(option)}
                  onChange={(e) => {
                    const currentValues = checkboxValues;
                    if (e.target.checked) {
                      handleInputChange(field.name, [...currentValues, option]);
                    } else {
                      handleInputChange(
                        field.name,
                        currentValues.filter(val => val !== option)
                      );
                    }
                  }}
                />
                <span>{option}</span>
              </CheckboxItem>
            ))}
          </CheckboxGroup>
        );

      case 'calculation':
        return (
          <CalculationField
            field={field}
            value={formData[field.name] || 0}
            onChange={(value) => handleInputChange(field.name, value)}
            formData={formData}
            showFormula={true}
            showHelp={true}
          />
        );

      case 'table':
        return (
          <TableField
            field={field}
            value={formData[field.name] || []}
            onChange={(value) => handleInputChange(field.name, value)}
            formData={formData}
          />
        );

      case 'section':
        console.log('FormPreview: Processing section field:', field.label, 'Type:', field.type);
        const sectionHeadingStyle = {
          fontSize: field.level === 1 ? '28px' : field.level === 2 ? '24px' : field.level === 3 ? '20px' : field.level === 4 ? '18px' : field.level === 5 ? '16px' : '14px',
          fontWeight: field.bold ? '700' : '600',
          fontStyle: field.italic ? 'italic' : 'normal',
          textDecoration: field.underline ? 'underline' : 'none',
          fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
          color: field.textColor || '#1f2937',
          textAlign: field.alignment || 'left',
          margin: '0 0 8px 0',
          padding: '0'
        };
        
        // Debug section styling
        console.log('FormPreview: Rendering section field:', field.label);
        console.log('FormPreview: Section field data:', {
          bold: field.bold,
          alignment: field.alignment,
          fontFamily: field.fontFamily,
          textColor: field.textColor,
          level: field.level,
          italic: field.italic
        });
        console.log('FormPreview: Section heading style:', sectionHeadingStyle);
        
        return (
          <div style={{ margin: '24px 0 12px 0' }}>
            <div style={sectionHeadingStyle}>{field.label || field.question}</div>
            {field.sectionSubtitle && (
              <div style={{ color: '#6b7280', marginTop: '-8px', marginBottom: field.sectionDivider ? '12px' : '0' }}>
                {field.sectionSubtitle}
              </div>
            )}
            {field.sectionDivider && <div style={{ height: '1px', background: '#e5e7eb', marginTop: '12px' }} />}
          </div>
        );

      case 'subheading':
        const subheadingStyle = {
          fontSize: field.level === 1 ? '20px' : field.level === 2 ? '18px' : field.level === 3 ? '16px' : field.level === 4 ? '14px' : field.level === 5 ? '12px' : '14px',
          fontWeight: field.bold ? '600' : '500',
          fontStyle: field.italic ? 'italic' : 'normal',
          textDecoration: field.underline ? 'underline' : 'none',
          fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
          color: field.textColor || '#374151',
          textAlign: field.alignment || 'left',
          margin: '0 0 6px 0',
          padding: '0'
        };
        
        return (
          <div style={{ margin: '16px 0 8px 0' }}>
            <div style={subheadingStyle}>{field.label || field.question}</div>
          </div>
        );

      case 'content':
        return (
          <div style={{ 
            margin: '16px 0', 
            padding: '16px', 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#374151',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }}>
              {field.content || field.label || ''}
            </div>
          </div>
        );

      case 'matrix':
        return (
          <MatrixField
            field={field}
            value={formData[field.name] || {}}
            onChange={(value) => handleInputChange(field.name, value)}
          />
        );

      case 'slider':
        return (
          <div style={{
            fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
            fontWeight: field.bold ? '700' : 'normal',
            fontStyle: field.italic ? 'italic' : 'normal',
            paddingLeft: field.inputPaddingLeft || 0
          }}>
            <input
              type="range"
              min={field.min ?? 0}
              max={field.max ?? 10}
              step={field.step ?? 1}
              value={formData[field.name] ?? field.min ?? 0}
              onChange={(e) => handleInputChange(field.name, Number(e.target.value))}
              style={{ width: '100%' }}
            />
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{formData[field.name] ?? field.min ?? 0}</div>
          </div>
        );

      case 'file':
        return (
          <div>
            <FileUploadContainer
              onClick={() => document.getElementById(`file-${field.name}`).click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('drag-over');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('drag-over');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('drag-over');
                const droppedFiles = e.dataTransfer.files;
                handleFileUpload(field.name, droppedFiles);
              }}
            >
              <FileUploadInput
                id={`file-${field.name}`}
                type="file"
                multiple={field.multipleFiles}
                accept={formatAcceptAttribute(field.acceptedFileTypes)}
                onChange={(e) => handleFileUpload(field.name, e.target.files)}
              />
              <FileUploadText>
                <FileUploadIcon>
                  <FiUpload />
                </FileUploadIcon>
                <div>
                  <strong>Click to upload</strong> or drag and drop
                </div>
                <div style={{ fontSize: '14px' }}>
                  {field.acceptedFileTypes && field.acceptedFileTypes.length > 0 && (
                    <>Accepted types: {field.acceptedFileTypes.join(', ')}</>
                  )}
                  {field.maxFileSize && (
                    <> • Max size: {formatFileSize(field.maxFileSize * 1024 * 1024)}</>
                  )}
                </div>
              </FileUploadText>
            </FileUploadContainer>
            <FileList>
              {(uploadedFiles[field.name] || []).map((file, index) => (
                <FileItem key={index}>
                  <FileInfo>
                    <FiFile />
                    <div>
                      <FileName>{file.name}</FileName>
                      <FileSize>{formatFileSize(file.size)}</FileSize>
                    </div>
                  </FileInfo>
                  <RemoveFileButton onClick={() => removeFile(field.name, index)}>
                    Remove
                  </RemoveFileButton>
                </FileItem>
              ))}
            </FileList>
          </div>
        );

      case 'rating':
        const rating = formData[field.name] || 0;
        const maxRating = field.maxRating || 5;
        const ratingLabels = field.ratingLabels || [];
        const ratingIcon = field.ratingIcon || 'star';
        // Use Unicode characters that respond to CSS color where possible
        const iconMap = { 
          star: { filled: '★', empty: '☆' },
          heart: { filled: '❤', empty: '♡' },
          thumbs: { filled: '👍', empty: '👍' }, // Emoji - will use filter
          face: { filled: '😊', empty: '😊' }, // Emoji - will use filter
          fire: { filled: '🔥', empty: '🔥' }, // Emoji - will use filter
          check: { filled: '✔', empty: '☐' }
        };
        const emotionFaces = ['😢', '😐', '😊', '😄', '😍']; // Sad, Neutral, Happy, Very Happy, Love
        
        // Check if icon is an emoji that needs filter (doesn't respond to CSS color)
        const isEmojiIcon = (icon) => {
          return ['thumbs', 'face', 'fire'].includes(icon) || icon === 'emotion';
        };
        
        // Get icon per index, taking into account filled state
        const getIconForIndex = (ratingValue, isFilled) => {
          if (ratingIcon === 'emotion') {
            const faceIndex = Math.min(Math.max(ratingValue - 1, 0), emotionFaces.length - 1);
            return emotionFaces[faceIndex];
          }
          const iconSet = iconMap[ratingIcon] || iconMap.star;
          return isFilled ? iconSet.filled : iconSet.empty;
        };
        
        return (
          <div style={{ 
            marginBottom: '16px',
            fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
            fontWeight: field.bold ? '700' : 'normal',
            fontStyle: field.italic ? 'italic' : 'normal',
            paddingLeft: field.inputPaddingLeft || 0
          }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              {Array.from({ length: maxRating }, (_, index) => {
                const isFilled = index < rating;
                const iconNeedsFilter = isEmojiIcon(ratingIcon) || (ratingIcon === 'emotion');
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleInputChange(field.name, index + 1)}
                    style={{ 
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: index < rating ? '#fbbf24' : '#d1d5db',
                      transition: 'color 0.2s',
                      // For emoji icons, use filter to make them grey when not filled
                      ...(iconNeedsFilter && !isFilled ? {
                        filter: 'grayscale(100%) opacity(0.4)',
                        transition: 'filter 0.2s ease, color 0.2s'
                      } : {}),
                      ...(iconNeedsFilter && isFilled ? {
                        filter: 'none',
                        transition: 'filter 0.2s ease, color 0.2s'
                      } : {})
                    }}
                  >
                    {getIconForIndex(index + 1, isFilled)}
                  </button>
                );
              })}
            </div>
            {ratingLabels[rating - 1] && (
              <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                {ratingLabels[rating - 1]}
              </div>
            )}
          </div>
        );

      case 'repeatable':
        const instances = repeatableInstances[field.id] || [0];
        const continueStates = repeatableContinueStates[field.id] || {};

        const handleContinueResponse = (fieldId, instanceIndex, addAnother) => {
          setRepeatableContinueStates(prev => ({
            ...prev,
            [fieldId]: {
              ...prev[fieldId],
              [instanceIndex]: addAnother
            }
          }));

          if (addAnother) {
            setRepeatableInstances(prev => ({
              ...prev,
              [fieldId]: [...(prev[fieldId] || [0]), (prev[fieldId]?.length || 1)]
            }));
          }
        };

        const renderRepeatableField = (repeatableField, instanceIndex, fieldId) => {
          const fieldName = `${fieldId}_${instanceIndex}_${repeatableField.name}`;
          const fieldValue = formData[fieldName];

          switch (repeatableField.type) {
            case 'text':
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <FieldInput
                    type="text"
                    value={fieldValue || ''}
                    onChange={(e) => handleInputChange(fieldName, e.target.value)}
                    placeholder={`Enter ${repeatableField.label.toLowerCase()}`}
                    style={{ backgroundColor: 'white', width: '100%' }}
                  />
                </div>
              );

            case 'textarea':
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <FieldTextarea
                    value={fieldValue || ''}
                    onChange={(e) => handleInputChange(fieldName, e.target.value)}
                    placeholder={`Enter ${repeatableField.label.toLowerCase()}`}
                    rows={4}
                    style={{ backgroundColor: 'white', width: '100%' }}
                  />
                </div>
              );

            case 'heading':
              const headingStyle = {
                fontSize: repeatableField.level === 1 ? '28px' : repeatableField.level === 2 ? '24px' : repeatableField.level === 3 ? '20px' : repeatableField.level === 4 ? '18px' : repeatableField.level === 5 ? '16px' : '14px',
                fontWeight: '700',
                color: '#1f2937',
                margin: '16px 0 12px 0',
                padding: '0'
              };
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <div style={headingStyle}>{repeatableField.label || 'Heading'}</div>
                </div>
              );

            case 'subheading':
              const subheadingStyle = {
                fontSize: repeatableField.level === 1 ? '20px' : repeatableField.level === 2 ? '18px' : repeatableField.level === 3 ? '16px' : repeatableField.level === 4 ? '14px' : repeatableField.level === 5 ? '12px' : '14px',
                fontWeight: '600',
                color: '#374151',
                margin: '12px 0 8px 0',
                padding: '0'
              };
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <div style={subheadingStyle}>{repeatableField.label || 'Subheading'}</div>
                </div>
              );

            case 'rating':
              const rating = fieldValue || 0;
              const maxRating = repeatableField.maxRating || 5;
              const ratingLabels = repeatableField.ratingLabels || [];
              const ratingIcon = repeatableField.ratingIcon || 'star';
              // Use Unicode characters that respond to CSS color where possible
              const iconMap = { 
                star: { filled: '★', empty: '☆' },
                heart: { filled: '❤', empty: '♡' },
                thumbs: { filled: '👍', empty: '👍' }, // Emoji - will use filter
                face: { filled: '😊', empty: '😊' }, // Emoji - will use filter
                fire: { filled: '🔥', empty: '🔥' }, // Emoji - will use filter
                check: { filled: '✔', empty: '☐' }
              };
              const emotionFaces = ['😢', '😐', '😊', '😄', '😍'];
              
              // Check if icon is an emoji that needs filter (doesn't respond to CSS color)
              const isEmojiIcon = (icon) => {
                return ['thumbs', 'face', 'fire'].includes(icon) || icon === 'emotion';
              };
              
              // Get icon per index, taking into account filled state
              const getIconForIndex = (ratingValue, isFilled) => {
                if (ratingIcon === 'emotion') {
                  const faceIndex = Math.min(Math.max(ratingValue - 1, 0), emotionFaces.length - 1);
                  return emotionFaces[faceIndex];
                }
                const iconSet = iconMap[ratingIcon] || iconMap.star;
                return isFilled ? iconSet.filled : iconSet.empty;
              };
              
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {Array.from({ length: maxRating }, (_, index) => {
                      const isFilled = index < rating;
                      const iconNeedsFilter = isEmojiIcon(ratingIcon) || (ratingIcon === 'emotion');
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleInputChange(fieldName, index + 1)}
                          style={{
                            fontSize: '24px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            color: isFilled ? '#fbbf24' : '#d1d5db',
                            transition: 'color 0.2s ease, filter 0.2s ease',
                            transform: isFilled ? 'scale(1.1)' : 'scale(1)',
                            // For emoji icons, use filter to make them grey when not filled
                            ...(iconNeedsFilter && !isFilled ? {
                              filter: 'grayscale(100%) opacity(0.4)',
                            } : {}),
                            ...(iconNeedsFilter && isFilled ? {
                              filter: 'none',
                            } : {})
                          }}
                        >
                          {getIconForIndex(index + 1, isFilled)}
                        </button>
                      );
                    })}
                  </div>
                  {rating > 0 && (
                    <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                      Selected: {rating}
                      {ratingLabels[rating - 1] && ` (${ratingLabels[rating - 1]})`}
                    </div>
                  )}
                </div>
              );

            case 'number':
            case 'email':
            case 'date':
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <FieldInput
                    type={repeatableField.type}
                    value={fieldValue || ''}
                    onChange={(e) => handleInputChange(fieldName, e.target.value)}
                    placeholder={repeatableField.placeholder || `Enter ${repeatableField.label.toLowerCase()}`}
                    style={{ backgroundColor: 'white', width: '100%' }}
                  />
                </div>
              );

            case 'checkbox':
              const checkboxValues = fieldValue || [];
              const repeatableCheckboxLayout = repeatableField.checkboxLayout || 'vertical';
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: repeatableCheckboxLayout === 'horizontal' ? 'row' : 'column',
                    flexWrap: repeatableCheckboxLayout === 'horizontal' ? 'wrap' : 'nowrap',
                    gap: '8px' 
                  }}>
                    {repeatableField.options?.map((option, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          value={option}
                          checked={checkboxValues.includes(option)}
                          onChange={(e) => {
                            const currentValues = checkboxValues;
                            if (e.target.checked) {
                              handleInputChange(fieldName, [...currentValues, option]);
                            } else {
                              handleInputChange(
                                fieldName,
                                currentValues.filter(val => val !== option)
                              );
                            }
                          }}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span>{option}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );

            case 'select':
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <select
                    value={fieldValue || ''}
                    onChange={(e) => handleInputChange(fieldName, e.target.value)}
                    style={{ 
                      backgroundColor: 'white',
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select {repeatableField.label.toLowerCase()}</option>
                    {repeatableField.options?.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              );

            case 'mapLocation': {
              const mapVal = fieldValue && typeof fieldValue === 'object' && typeof fieldValue.lat === 'number' && typeof fieldValue.lng === 'number'
                && !Number.isNaN(fieldValue.lat) && !Number.isNaN(fieldValue.lng)
                ? fieldValue
                : null;
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <MapLocationField
                    value={mapVal}
                    onChange={() => {}}
                    disabled
                  />
                </div>
              );
            }

            default:
              return null;
          }
        };

        return (
          <>
            {instances.map((instanceIndex) => {
              const instanceKey = `${field.id}_instance_${instanceIndex}`;
              const canContinue = instanceIndex === instances.length - 1 && continueStates[instanceIndex] === undefined;

              return (
                <React.Fragment key={instanceKey}>
                  {instances.length > 1 && instanceIndex > 0 && (
                    <div style={{
                      marginTop: '32px',
                      marginBottom: '24px',
                      height: '1px',
                      backgroundColor: '#e5e7eb'
                    }} />
                  )}
                  
                  {instances.length > 1 && (
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6b7280',
                      marginBottom: '16px',
                      marginTop: instanceIndex > 0 ? '0' : '0'
                    }}>
                      {field.label || 'Repeatable Group'} #{instanceIndex + 1}
                    </div>
                  )}

                  {field.repeatableFields?.map((repeatableField) =>
                    renderRepeatableField(repeatableField, instanceIndex, field.id)
                  )}

                  {canContinue && field.showContinueButton !== false && (
                    <div style={{
                      marginTop: '24px',
                      marginBottom: '32px',
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '12px'
                      }}>
                        {field.continueQuestion || 'Do you want to add another?'}
                      </div>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                          type="button"
                          onClick={() => handleContinueResponse(field.id, instanceIndex, true)}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                        >
                          {field.continueYesLabel || 'Yes, add another'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleContinueResponse(field.id, instanceIndex, false)}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                        >
                          {field.continueNoLabel || 'No, submit form'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Add "Add Another" button when continue button is hidden */}
                  {canContinue && field.showContinueButton === false && (
                    <div style={{
                      marginTop: '24px',
                      marginBottom: '16px'
                    }}>
                      <button
                        type="button"
                        onClick={() => handleContinueResponse(field.id, instanceIndex, true)}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        {field.continueYesLabel || 'Add Another'}
                      </button>
                    </div>
                  )}

                  {/* Add "Add Another" button when continue button is hidden */}
                  {canContinue && field.showContinueButton === false && (
                    <div style={{
                      marginTop: '24px',
                      marginBottom: '16px'
                    }}>
                      <button
                        type="button"
                        onClick={() => handleContinueResponse(field.id, instanceIndex, true)}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        {field.continueYesLabel || 'Add Another'}
                      </button>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );

      default:
        console.log('FormPreview: Unhandled field type:', field.type, 'for field:', field.label);
        return (
          <FieldInput
            type="text"
            {...fieldProps}
            placeholder={field.placeholder}
          />
        );
    }
  };

  if (loading) {
    return (
      <FormContainer>
        <LoadingSpinner>Loading form...</LoadingSpinner>
      </FormContainer>
    );
  }

  if (!form) {
    console.log('Form is null, showing error message');
    console.log('Current id:', id);
    console.log('isPreview:', isPreview);
    console.log('loading:', loading);
    return (
      <FormContainer>
        <ErrorMessage>
          Form not found or is no longer active.
          {id === 'preview' && (
            <div style={{ fontSize: '14px', marginTop: '12px' }}>
              Tip: Make sure to click the Preview button in the Questionnaire Builder.
            </div>
          )}
        </ErrorMessage>
      </FormContainer>
    );
  }

  if (submitted && !isPreview) {
    return (
      <FormContainer>
        <FormCard>
          <SuccessMessage>
            <SuccessIcon>
              <FiCheck />
            </SuccessIcon>
            <h2>Thank you!</h2>
            <p>Your response has been submitted successfully.</p>
          </SuccessMessage>
        </FormCard>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <BackButton 
        onClick={() => {
          if (isPreview) {
            // If opened from form builder preview, try to close window or go to builder
            if (window.opener) {
              window.close();
            } else {
              // Fallback: navigate to form builder if it's a preview
              const formId = id === 'preview' ? null : id;
              navigate(formId ? `/builder/${formId}` : '/builder');
            }
          } else {
            // If navigating from within the app, go back to dashboard
            navigate('/');
          }
        }}
        title={isPreview ? "Close preview window" : "Go back to dashboard"}
      >
        <FiArrowLeft />
        {isPreview ? "Close Preview" : "Back"}
      </BackButton>

      <FormCard>
        <LogoContainer>
          {form.logo_url ? (
            <img 
              src={form.logo_url} 
              alt="Company Logo" 
              onError={(e) => {
                console.error('Failed to load logo:', form.logo_url);
                e.target.style.display = 'none';
              }}
              onLoad={() => console.log('Logo loaded successfully:', form.logo_url)}
            />
          ) : (
            <DefaultLogo>
              {form.title.charAt(0).toUpperCase()}
            </DefaultLogo>
          )}
        </LogoContainer>
        
        <FormTitle>{form.title}</FormTitle>
        {form.description && (
          <FormDescription>{form.description}</FormDescription>
        )}

        {isPreview && (
          <div style={{ 
            background: '#fef3c7', 
            border: '1px solid #f59e0b', 
            borderRadius: '6px', 
            padding: '12px', 
            marginBottom: '24px',
            textAlign: 'center',
            color: '#92400e'
          }}>
            <strong>Preview Mode</strong> - This is how your form will look to users
          </div>
        )}

        {/* Step Progress Indicator */}
        {form && isMultiStep && totalSteps > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '32px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151'
            }}>
              Step {currentStep + 1} of {totalSteps}
            </div>
            <div style={{
              display: 'flex',
              gap: '8px',
              marginLeft: '16px'
            }}>
              {Array.from({ length: totalSteps }, (_, index) => (
                <div
                  key={index}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: index <= currentStep ? '#3b82f6' : '#d1d5db',
                    transition: 'background-color 0.2s ease'
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {form && isQuestionnaire(form) ? (
            <div>
              {/* Render form fields first if they exist - only show on intro step or non-multi-step */}
              {form?.form_structure?.formFields && form.form_structure.formFields.length > 0 && (!isMultiStep || showIntroOnly) && (
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ 
                    fontSize: '18px', 
                    fontWeight: '700', 
                    color: '#1f2937', 
                    marginBottom: '20px',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #e5e7eb'
                  }}>
                    Your Information
                  </h3>
                  
                  {/* Render fields in grid based on width */}
                  <FieldsGrid>
                    {form.form_structure.formFields.map((field) => {
                      const fieldKey = field.name || field.id;
                      const currentValue = formData[fieldKey];

                      return (
                        <FormField 
                          key={fieldKey}
                          fieldWidth={field.fieldWidth || field.width || 'full'}
                          fieldType={field.type}
                        >
                          <FieldLabel 
                            required={field.required} 
                            fontSize={form?.labelFontSize || form?.label_font_size || '14px'}
                            fontFamily={field.fontFamily}
                            bold={field.bold}
                            italic={field.italic}
                          >
                            {field.label || field.question}
                          </FieldLabel>
                          <FieldInput
                            type={field.type}
                            value={currentValue || ''}
                            onChange={(e) => handleInputChange(field.name || field.id, e.target.value)}
                            placeholder={field.label}
                          />
                        </FormField>
                      );
                    })}
                  </FieldsGrid>
                  
                  <div style={{ 
                    height: '1px', 
                    background: '#e5e7eb', 
                    margin: '32px 0',
                  }} />
                </div>
              )}

              {/* Render questions - only show if not on intro step or if not multi-step */}
              {form?.form_structure?.questions && form.form_structure.questions.length > 0 && (!isMultiStep || !showIntroOnly) ? (
                <div>
                  <h3 style={{ 
                    fontSize: '18px', 
                    fontWeight: '700', 
                    color: '#1f2937', 
                    marginBottom: '20px'
                  }}>
                    Answer these questions
                  </h3>
                  {visibleFields.map((question, index) => {
                    const questionKey = question.id;
                    const currentValue = formData[questionKey];
                    
                    return (
                      <div key={questionKey} style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        marginBottom: '24px',
                        border: '2px solid #e5e7eb'
                      }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#6b7280',
                          marginBottom: '12px'
                        }}>
                          Question {isMultiStep ? (questionIndex + 1) : (index + 1)} of {groupSectionsWithQuestions ? stepGroups.length : filteredFields.length}
                        </div>
                        
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1f2937',
                          marginBottom: '16px',
                          lineHeight: '1.5'
                        }}>
                          {question.label || question.question}
                          {question.required && <span style={{ color: '#ef4444' }}> *</span>}
                        </div>

                        {/* Multiple Choice / True-False */}
                        {(question.type === 'multiple_choice' || question.type === 'true_false') && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {question.options?.map((option) => (
                              <label 
                                key={option.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '12px 16px',
                                  background: currentValue === option.text ? '#f0f9ff' : '#f9fafb',
                                  border: `2px solid ${currentValue === option.text ? '#3b82f6' : '#e5e7eb'}`,
                                  borderRadius: '8px',
                                  cursor: 'pointer'
                                }}
                              >
                                <input
                                  type="radio"
                                  name={questionKey}
                                  value={option.text}
                                  checked={currentValue === option.text}
                                  onChange={(e) => handleInputChange(questionKey, e.target.value)}
                                  style={{ marginRight: '12px', width: '18px', height: '18px' }}
                                />
                                <span style={{ fontSize: '15px', color: '#374151' }}>
                                  {option.text}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}

                        {/* Checkbox */}
                        {question.type === 'checkbox' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {question.options?.map((option) => {
                              const selectedValues = Array.isArray(currentValue) ? currentValue : [];
                              const isChecked = selectedValues.includes(option.text);
                              
                              return (
                                <label 
                                  key={option.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '12px 16px',
                                    background: isChecked ? '#f0f9ff' : '#f9fafb',
                                    border: `2px solid ${isChecked ? '#3b82f6' : '#e5e7eb'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    value={option.text}
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const newValues = isChecked
                                        ? selectedValues.filter(v => v !== option.text)
                                        : [...selectedValues, option.text];
                                      handleInputChange(questionKey, newValues);
                                    }}
                                    style={{ marginRight: '12px', width: '18px', height: '18px' }}
                                  />
                                  <span style={{ fontSize: '15px', color: '#374151' }}>
                                    {option.text}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}

                        {/* Short Answer */}
                        {question.type === 'short_answer' && (
                          <FieldTextarea
                            value={currentValue || ''}
                            onChange={(e) => handleInputChange(questionKey, e.target.value)}
                            placeholder="Enter your answer here..."
                            style={{ marginTop: '12px' }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <p>This questionnaire has no questions configured yet.</p>
                </div>
              )}
            </div>
          ) : form && form?.form_structure && Array.isArray(form.form_structure) ? (() => {
            // For multi-step forms with section grouping, show the current group
            if (isMultiStep && showQuestionsOnly && groupSectionsWithQuestions) {
              const stepIndex = hasIntroSection ? currentStep - 1 : currentStep;
              const currentGroup = stepGroups[stepIndex];
              if (!currentGroup) return null;
              
              return (
                <div>
                  {/* Show section header if it exists */}
                  {currentGroup.section && (
                    <div style={{ margin: '24px 0 12px 0' }}>
                      {(() => {
                        const section = currentGroup.section;
                        const sectionHeadingStyle = {
                          fontSize: section.level === 1 ? '28px' : section.level === 2 ? '24px' : section.level === 3 ? '20px' : section.level === 4 ? '18px' : section.level === 5 ? '16px' : '14px',
                          fontWeight: section.bold ? '700' : '600',
                          fontStyle: section.italic ? 'italic' : 'normal',
                          textDecoration: section.underline ? 'underline' : 'none',
                          fontFamily: section.fontFamily && section.fontFamily !== 'default' ? section.fontFamily : 'inherit',
                          color: section.textColor || '#1f2937',
                          textAlign: section.alignment || 'left',
                          margin: '0 0 8px 0',
                          padding: '0'
                        };
                        
                        console.log('FormPreview: Rendering grouped section field:', section.label);
                        console.log('FormPreview: Grouped section field data:', {
                          bold: section.bold,
                          alignment: section.alignment,
                          fontFamily: section.fontFamily,
                          textColor: section.textColor,
                          level: section.level,
                          italic: section.italic
                        });
                        console.log('FormPreview: Grouped section heading style:', sectionHeadingStyle);
                        
                        return (
                          <div style={sectionHeadingStyle}>{section.label}</div>
                        );
                      })()}
                      {currentGroup.section.sectionSubtitle && (
                        <div style={{ color: '#6b7280', marginTop: '-8px', marginBottom: currentGroup.section.sectionDivider ? '12px' : '0' }}>
                          {currentGroup.section.sectionSubtitle}
                        </div>
                      )}
                      {currentGroup.section.sectionDivider && <div style={{ height: '1px', background: '#e5e7eb', marginTop: '12px' }} />}
                    </div>
                  )}
                  
                  {/* Render all fields in this group */}
                  <FieldsGrid>
                    {currentGroup.fields.map(field => {
                      // Special rendering for heading fields
                      if (field.type === 'heading') {
                        const headingStyle = {
                          fontSize: field.level === 1 ? '28px' : field.level === 2 ? '24px' : field.level === 3 ? '20px' : field.level === 4 ? '18px' : field.level === 5 ? '16px' : '14px',
                          fontWeight: field.bold ? '700' : '600',
                          fontStyle: field.italic ? 'italic' : 'normal',
                          textDecoration: field.underline ? 'underline' : 'none',
                          fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
                          color: field.textColor || '#1f2937',
                          textAlign: field.alignment || 'left',
                          margin: '24px 0 12px 0',
                          padding: '0',
                          gridColumn: 'span 12' // Always full width
                        };
                        return (
                          <div key={field.id} style={headingStyle}>
                            {field.label}
                          </div>
                        );
                      }
                      
                      // Special rendering for subheading fields
                      if (field.type === 'subheading') {
                        const subheadingStyle = {
                          fontSize: field.level === 1 ? '22px' : field.level === 2 ? '20px' : field.level === 3 ? '18px' : field.level === 4 ? '16px' : field.level === 5 ? '15px' : '14px',
                          fontWeight: field.bold ? '600' : '500',
                          fontStyle: field.italic ? 'italic' : 'normal',
                          textDecoration: field.underline ? 'underline' : 'none',
                          fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
                          color: field.textColor || '#4b5563',
                          textAlign: field.alignment || 'left',
                          margin: '16px 0 8px 0',
                          padding: '0',
                          gridColumn: 'span 12' // Always full width
                        };
                        return (
                          <div key={field.id} style={subheadingStyle}>
                            {field.label || field.question}
                          </div>
                        );
                      }
                      
                      return (
                        <FormField 
                          key={field.id}
                          fieldWidth={field.fieldWidth || 'full'}
                          fieldType={field.type}
                        >
                          <FieldLabel 
                            required={field.required} 
                            fontSize={form?.labelFontSize || form?.label_font_size || '14px'}
                            fontFamily={field.fontFamily}
                            bold={field.bold}
                            italic={field.italic}
                          >
                            {field.label || field.question}
                          </FieldLabel>
                          {renderField(field)}
                          {errors[field.name] && (
                            <ErrorMessage>{errors[field.name]}</ErrorMessage>
                          )}
                        </FormField>
                      );
                    })}
                  </FieldsGrid>
                </div>
              );
            }
            
            // For multi-step forms without section grouping, show single field with section header
            if (isMultiStep && showQuestionsOnly && !groupSectionsWithQuestions) {
              // Determine the field to display: skip headings/subheadings and pick next non-heading field if needed
              const allFieldsWithVisibility = allFields.filter(field => field.visible !== false);
              const initialField = filteredFields[questionIndex];
              if (!initialField) return null;
              const initialIndex = allFieldsWithVisibility.findIndex(f => f.id === initialField.id);
              let displayIndex = initialIndex;
              while (
                displayIndex < allFieldsWithVisibility.length &&
                (allFieldsWithVisibility[displayIndex].type === 'heading' ||
                 allFieldsWithVisibility[displayIndex].type === 'subheading' ||
                 allFieldsWithVisibility[displayIndex].type === 'section')
              ) {
                displayIndex++;
              }
              const displayField = allFieldsWithVisibility[displayIndex] || initialField;

              // Find any section header immediately before the display field
              const sectionsBeforeDisplay = allFieldsWithVisibility
                .slice(0, displayIndex)
                .filter(f => f.type === 'section');
              const lastSectionBeforeDisplay = sectionsBeforeDisplay[sectionsBeforeDisplay.length - 1];

              return (
                <div>
                  {/* Show the last section header before display field if it exists */}
                  {lastSectionBeforeDisplay && (
                    <div style={{ margin: '24px 0 12px 0' }}>
                      {(() => {
                        const section = lastSectionBeforeDisplay;
                        const sectionHeadingStyle = {
                          fontSize: section.level === 1 ? '28px' : section.level === 2 ? '24px' : section.level === 3 ? '20px' : section.level === 4 ? '18px' : section.level === 5 ? '16px' : '14px',
                          fontWeight: section.bold ? '700' : '600',
                          fontStyle: section.italic ? 'italic' : 'normal',
                          textDecoration: section.underline ? 'underline' : 'none',
                          fontFamily: section.fontFamily && section.fontFamily !== 'default' ? section.fontFamily : 'inherit',
                          color: section.textColor || '#1f2937',
                          textAlign: section.alignment || 'left',
                          margin: '0 0 8px 0',
                          padding: '0'
                        };
                        
                        console.log('FormPreview: Rendering last section field:', section.label);
                        console.log('FormPreview: Last section field data:', {
                          bold: section.bold,
                          alignment: section.alignment,
                          fontFamily: section.fontFamily,
                          textColor: section.textColor,
                          level: section.level,
                          italic: section.italic
                        });
                        console.log('FormPreview: Last section heading style:', sectionHeadingStyle);
                        
                        return (
                          <div style={sectionHeadingStyle}>{section.label}</div>
                        );
                      })()}
                      {lastSectionBeforeDisplay.sectionSubtitle && (
                        <div style={{ color: '#6b7280', marginTop: '-8px', marginBottom: lastSectionBeforeDisplay.sectionDivider ? '12px' : '0' }}>
                          {lastSectionBeforeDisplay.sectionSubtitle}
                        </div>
                      )}
                      {lastSectionBeforeDisplay.sectionDivider && <div style={{ height: '1px', background: '#e5e7eb', marginTop: '12px' }} />}
                    </div>
                  )}
                  {/* Render any subheadings between the last section and current field */}
                  {(() => {
                    const startIndex = lastSectionBeforeDisplay
                      ? allFieldsWithVisibility.findIndex(f => f.id === lastSectionBeforeDisplay.id) + 1
                      : 0;
                    const subheadingsBetween = allFieldsWithVisibility
                      .slice(startIndex, displayIndex)
                      .filter(f => f.type === 'subheading');

                    return subheadingsBetween.map(sh => {
                      const subheadingStyle = {
                        fontSize: sh.level === 1 ? '22px' : sh.level === 2 ? '20px' : sh.level === 3 ? '18px' : sh.level === 4 ? '16px' : sh.level === 5 ? '15px' : '14px',
                        fontWeight: sh.bold ? '600' : '500',
                        fontStyle: sh.italic ? 'italic' : 'normal',
                        textDecoration: sh.underline ? 'underline' : 'none',
                        fontFamily: sh.fontFamily && sh.fontFamily !== 'default' ? sh.fontFamily : 'inherit',
                        color: sh.textColor || '#4b5563',
                        textAlign: sh.alignment || 'left',
                        margin: '16px 0 8px 0',
                        padding: '0'
                      };
                      return (
                        <div key={sh.id} style={subheadingStyle}>
                          {sh.label || sh.question}
                        </div>
                      );
                    });
                  })()}

                  {/* Render the current field with its label */}
                  <FormField key={displayField.id}>
                    <FieldLabel 
                      required={displayField.required} 
                      fontSize={form?.labelFontSize || form?.label_font_size || '14px'}
                      fontFamily={displayField.fontFamily}
                      bold={displayField.bold}
                      italic={displayField.italic}
                    >
                      {displayField.label || displayField.question}
                    </FieldLabel>
                    {renderField(displayField)}
                    {errors[displayField.name] && (
                      <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '4px' }}>
                        {errors[displayField.name]}
                      </div>
                    )}
                  </FormField>
                </div>
              );
            }
            
            // For non-multi-step forms, use the original grouping logic
            const groups = [];
            let current = { section: null, fields: [] };
            const items = visibleFields.filter(f => f && f.type !== 'heading');
            items.forEach((f) => {
              if (f.type === 'section') {
                if (current.section || current.fields.length > 0) groups.push(current);
                current = { section: f, fields: [] };
              } else {
                current.fields.push(f);
              }
            });
            if (current.section || current.fields.length > 0) groups.push(current);

            return groups.map((group, gi) => (
              <div key={group.section?.id || `group_${gi}`} style={{ 
                marginBottom: '24px',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                background: '#ffffff',
                padding: '16px'
              }}>
                {group.section && (
                  <div style={{ margin: '24px 0 12px 0' }}>
                    {(() => {
                      const section = group.section;
                      const sectionHeadingStyle = {
                        fontSize: section.level === 1 ? '28px' : section.level === 2 ? '24px' : section.level === 3 ? '20px' : section.level === 4 ? '18px' : section.level === 5 ? '16px' : '14px',
                        fontWeight: section.bold ? '700' : '600',
                        fontStyle: section.italic ? 'italic' : 'normal',
                        textDecoration: section.underline ? 'underline' : 'none',
                        fontFamily: section.fontFamily && section.fontFamily !== 'default' ? section.fontFamily : 'inherit',
                        color: section.textColor || '#1f2937',
                        textAlign: section.alignment || 'left',
                        margin: '0 0 8px 0',
                        padding: '0'
                      };
                      
                      console.log('FormPreview: Rendering group section field:', section.label);
                      console.log('FormPreview: Group section field data:', {
                        bold: section.bold,
                        alignment: section.alignment,
                        fontFamily: section.fontFamily,
                        textColor: section.textColor,
                        level: section.level,
                        italic: section.italic
                      });
                      console.log('FormPreview: Group section heading style:', sectionHeadingStyle);
                      
                      return (
                        <div style={sectionHeadingStyle}>{section.label}</div>
                      );
                    })()}
                    {group.section.sectionSubtitle && (
                      <div style={{ color: '#6b7280', marginTop: '-8px', marginBottom: group.section.sectionDivider ? '12px' : '0' }}>
                        {group.section.sectionSubtitle}
                      </div>
                    )}
                    {group.section.sectionDivider && <div style={{ height: '1px', background: '#e5e7eb', marginTop: '12px' }} />}
                  </div>
                )}

                <FieldsGrid>
                  {group.fields.map((field) => {
                    // Special rendering for subheading fields
                    if (field.type === 'subheading') {
                      const subheadingStyle = {
                        fontSize: field.level === 1 ? '22px' : field.level === 2 ? '20px' : field.level === 3 ? '18px' : field.level === 4 ? '16px' : field.level === 5 ? '15px' : '14px',
                        fontWeight: field.bold ? '600' : '500',
                        fontStyle: field.italic ? 'italic' : 'normal',
                        textDecoration: field.underline ? 'underline' : 'none',
                        fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
                        color: field.textColor || '#4b5563',
                        textAlign: field.alignment || 'left',
                        margin: '16px 0 8px 0',
                        padding: '0',
                        gridColumn: 'span 12' // Always full width
                      };
                      return (
                        <div key={field.id} style={subheadingStyle}>
                          {field.label || field.question}
                        </div>
                      );
                    }
                    
                    if (field.type === 'contact') {
                      return (
                        <div key={field.id} style={{ 
                          border: '2px solid #e0f2fe', 
                          borderRadius: '8px', 
                          padding: '20px', 
                          marginBottom: '24px',
                          backgroundColor: '#f8fafc',
                          gridColumn: 'span 12' // Always full width for contact blocks
                        }}>
                          <ContactToggleButton 
                            onClick={() => setContactBlockCollapsed(!contactBlockCollapsed)}
                          >
                            {contactBlockCollapsed ? <FiChevronDown /> : <FiChevronUp />}
                            <span>📞</span>
                            Contact Information
                            <span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>
                              ({field.contactFields?.length || 0} fields)
                            </span>
                          </ContactToggleButton>
                          
                          <ContactFieldsContainer collapsed={contactBlockCollapsed}>
                            {field.contactFields?.map((contactField) => (
                              <FormField key={contactField.id} style={{ marginBottom: '16px' }}>
                                <FieldLabel 
                                  required={contactField.required} 
                                  fontSize={form?.labelFontSize || form?.label_font_size || '14px'}
                                  fontFamily={contactField.fontFamily}
                                  bold={contactField.bold}
                                  italic={contactField.italic}
                                >
                                  {contactField.label || contactField.question}
                                </FieldLabel>
                                {renderField(contactField)}
                                {errors[contactField.name] && (
                                  <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '4px' }}>
                                    {errors[contactField.name]}
                                  </div>
                                )}
                              </FormField>
                            ))}
                          </ContactFieldsContainer>
                        </div>
                      );
                    }

                    return (
                      <FormField 
                        key={field.id}
                        fieldWidth={field.fieldWidth || 'full'}
                        fieldType={field.type}
                      >
                        <FieldLabel 
                          required={field.required} 
                          fontSize={form?.labelFontSize || form?.label_font_size || '14px'}
                          fontFamily={field.fontFamily}
                          bold={field.bold}
                          italic={field.italic}
                        >
                          {field.label || field.question}
                        </FieldLabel>
                        {renderField(field)}
                        {errors[field.name] && (
                          <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '4px' }}>
                            {errors[field.name]}
                          </div>
                        )}
                      </FormField>
                    );
                  })}
                </FieldsGrid>
              </div>
            ));
          })() : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <p>This form has no fields configured yet.</p>
            </div>
          )}

          {/* Multi-step Navigation */}
          {form && isMultiStep && totalSteps > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '40px',
              padding: '20px',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: currentStep === 0 ? '#e5e7eb' : 'white',
                  color: currentStep === 0 ? '#9ca3af' : '#374151',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <FiArrowLeft />
                Previous
              </button>

              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#6b7280'
              }}>
                Step {currentStep + 1} of {totalSteps}
              </div>

              {!isLastStep ? (
                <button
                  type="button"
                  onClick={handleNext}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#2563eb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#3b82f6';
                  }}
                >
                  Next
                  <FiChevronRight />
                </button>
              ) : (
                <button
                  type="button"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#059669';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#10b981';
                  }}
                >
                  <FiCheck />
                  Complete Preview
                </button>
              )}
            </div>
          )}

          {!isMultiStep && (
            <SubmitButton type="submit" disabled={submitting || isPreview}>
              {submitting ? 'Submitting...' : isPreview ? ((form?.submit_button_text && form.submit_button_text.trim()) ? `${form.submit_button_text.trim()} (Preview)` : 'Submit Form (Preview)') : ((form?.submit_button_text && form.submit_button_text.trim()) ? form.submit_button_text.trim() : 'Submit Form')}
            </SubmitButton>
          )}
        </form>
      </FormCard>
    </FormContainer>
  );
}

export default FormPreview;
